import React, { useMemo, useState, useLayoutEffect, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ExpandedState,
  PaginationState,
  ColumnSizingState,
  Row,
} from '@tanstack/react-table';
import { WidgetDataResult, WidgetClickPoint, WidgetChartConfig, WidgetDataRow } from '../types';
import { formatNumber } from './echartsUtils';
import styles from './ChartTable.module.scss';
import sortDefault from '../assets/defaultSorting.svg';
import sortAsc from '../assets/sortingAscending.svg';
import sortDesc from '../assets/sortingDescending.svg';
import { observeResize } from '../observeResize';

interface ChartTableProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
  onPointClick?: (point: WidgetClickPoint) => void;
  /** Current page (1-based). When provided with onPageChange, enables server-side pagination. */
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Width of the expander toggle column in pixels */
const EXPANDER_W = 32;
/** Width of each data column in pixels (used for sticky left offset calculation) */
const DATA_COL_W = 140;

function isIdColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return lower === 'id' || lower.endsWith('_id');
}

function isUrl(val: string): boolean {
  return val.startsWith('http://') || val.startsWith('https://');
}


function formatValue(val: string | number | boolean | null | undefined, col?: string): React.ReactNode {
  if (val === null || val === undefined) return '\u2014';
  if (typeof val === 'number') {
    return col && isIdColumn(col) ? String(val) : formatNumber(val);
  }
  if (typeof val === 'string' && isUrl(val)) {
    return (
      <a href={val} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
        Link
      </a>
    );
  }
  return String(val);
}

function hexWithAlpha(hex: string, alpha: number): string {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
}


export const ChartTable: React.FC<ChartTableProps> = ({
  data,
  config,
  onPointClick,
  page: serverPage,
  pageSize: serverPageSize,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const tc = config.tableConfig ?? {};
  // freezeColumnIndex is 1-based: 1 = freeze first data col, 2 = first two, etc.
  // 0 or undefined = no freeze
  const freezeColumnIndex = tc.freezeColumnIndex ?? 0;
  const freezeHeader = tc.freezeHeader ?? true;
  const freezeFooter = tc.freezeFooter ?? false;
  const showFooter = tc.showFooter ?? false;
  const defaultPageSize = tc.pageSize ?? 10;
  const columnColors = tc.columnColors ?? {};
  const columnWidths = useMemo(() => tc.columnWidths ?? {}, [tc.columnWidths]);

  // Apply column ordering: listed columns first, remaining in original order
  const orderedColumns = useMemo(() => {
    const order = tc.columnOrder;
    if (!order?.length) return data.columns;
    const rest = data.columns.filter((c) => !order.includes(c));
    return [...order.filter((c) => data.columns.includes(c)), ...rest];
  }, [data.columns, tc.columnOrder]);

  const hasFrozenCols = freezeColumnIndex > 0;

  // Server-side pagination mode: active when page + onPageChange are provided externally
  const isServerPaginated = serverPage != null && serverPageSize != null && onPageChange != null;
  const serverTotalRows = data.totalRows ?? null;
  const serverTotalPages = isServerPaginated && serverTotalRows != null
    ? Math.max(1, Math.ceil(serverTotalRows / serverPageSize!))
    : 1;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [wrapperWidth, setWrapperWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    return observeResize(el, (entries) => {
      setWrapperWidth(entries[0]?.contentRect.width);
    });
  }, []);

  // Convert percentage columnWidths to pixels based on container width after mount
  useLayoutEffect(() => {
    if (!wrapperRef.current || !Object.keys(columnWidths).length) return;
    const containerWidth = wrapperRef.current.offsetWidth;
    const sizing: ColumnSizingState = {};
    Object.entries(columnWidths).forEach(([col, pct]) => {
      sizing[col] = Math.round((pct / 100) * containerWidth);
    });
    setColumnSizing(sizing);
  }, [columnWidths]);

  // Footer aggregates: sum for all-numeric columns, row count for first column
  const footerAggregates = useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    orderedColumns.forEach((col, idx) => {
      if (idx === 0) {
        result[col] = `${data.totalRows ?? data.rows.length} rows`;
        return;
      }
      const nums = data.rows.map((r) => r[col]).filter((v) => typeof v === 'number') as number[];
      result[col] =
        nums.length === data.rows.length && nums.length > 0
          ? nums.reduce((a, b) => a + b, 0).toLocaleString()
          : '';
    });
    return result;
  }, [data, orderedColumns]);

  const columns = useMemo<ColumnDef<WidgetDataRow>[]>(() => {
    const expanderCol: ColumnDef<WidgetDataRow> = {
      id: '__expander',
      enableSorting: false,
      size: EXPANDER_W,
      header: () => null,
      cell: ({ row }) => (
        <button
          className={styles.expandBtn}
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
          aria-label={row.getIsExpanded() ? t('Collapse row details') : t('Expand row details')}
          aria-expanded={row.getIsExpanded()}
        >
          <span aria-hidden="true">{row.getIsExpanded() ? '▾' : '▸'}</span>
        </button>
      ),
      footer: () => null,
    };

    const dataCols: ColumnDef<WidgetDataRow>[] = orderedColumns.map((col, idx) => ({
      id: col,
      accessorKey: col,
      size: columnWidths[col] ?? DATA_COL_W,
      minSize: 40,
      // Disable client-side sorting when the server owns pagination/sorting
      enableSorting: !isServerPaginated,
      header: col,
      cell: ({ getValue }) => formatValue(getValue() as string | number | boolean | null, col),
      footer: () =>
        showFooter ? (idx === 0 ? t('Total') : (footerAggregates[col] ?? '')) : null,
    }));

    return [expanderCol, ...dataCols];
  }, [orderedColumns, columnWidths, showFooter, footerAggregates, isServerPaginated]);

  const table = useReactTable({
    data: data.rows,
    columns,
    state: { sorting, expanded, pagination, columnSizing },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    ...(isServerPaginated
      ? { manualPagination: true, pageCount: serverTotalPages }
      : { getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel() }),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const handleRowClick = (row: Row<WidgetDataRow>) => {
    if (!onPointClick) return;
    const keyCol = orderedColumns[0];
    const valCol = orderedColumns[1] ?? orderedColumns[0];
    onPointClick({
      label: String(keyCol ?? ''),
      key: String(row.original[keyCol] ?? ''),
      value: (() => { const v = row.original[valCol] ?? ''; return typeof v === 'boolean' ? String(v) : v; })(),
      extra: { ...row.original } as Record<string, string | number | boolean | null>,
    });
  };

  const { pageIndex, pageSize } = table.getState().pagination;

  // Pagination display values differ between server-side and client-side modes
  const totalRows = isServerPaginated
    ? (serverTotalRows ?? data.rows.length)
    : data.rows.length;
  const currentPageSize = isServerPaginated ? serverPageSize! : pageSize;
  const currentPageIndex = isServerPaginated ? serverPage! - 1 : pageIndex;
  const firstRow = currentPageIndex * currentPageSize + 1;
  const lastRow = Math.min((currentPageIndex + 1) * currentPageSize, totalRows);
  const totalPages = isServerPaginated ? serverTotalPages : table.getPageCount();

  // Whether internal colIdx should be frozen (expander = 0, data cols start at 1)
  const isFrozen = (colIdx: number) => hasFrozenCols && colIdx <= freezeColumnIndex;

  // Compute cumulative left offsets from actual (possibly resized) column sizes
  const frozenLeftOffsets = useMemo(() => {
    const headers = table.getHeaderGroups()[0]?.headers ?? [];
    const offsets: number[] = [];
    let acc = 0;
    headers.forEach((h) => { offsets.push(acc); acc += h.getSize(); });
    return offsets;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSizing, columns]);

  const frozenStyle = (colIdx: number, size: number): React.CSSProperties => ({
    position: 'sticky',
    left: frozenLeftOffsets[colIdx] ?? 0,
    zIndex: 1,
    width: size,
    minWidth: size,
    maxWidth: size,
  });

  // z-index 3 for frozen header cells (above scrolling body)
  const frozenHeaderStyle = (colIdx: number, size: number): React.CSSProperties => ({
    ...frozenStyle(colIdx, size),
    zIndex: 3,
  });

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.tableContainer}>
        <table
          className={styles.table}
          role="grid"
          aria-rowcount={totalRows + 1}
          aria-label={t('Data table')}
        >
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header, colIdx) => {
                  const isExpander = header.column.id === '__expander';
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  const frozen = isFrozen(colIdx);

                  const size = header.getSize();

                  if (isExpander) {
                    return (
                      <th
                        key={header.id}
                        className={[
                          styles.thExpander,
                          freezeHeader ? styles.stickyTop : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={frozen ? frozenHeaderStyle(0, size) : { width: size, minWidth: size }}
                        scope="col"
                        aria-label={t('Row details')}
                      />
                    );
                  }

                  return (
                    <th
                      key={header.id}
                      className={[
                        styles.th,
                        canSort ? styles.sortable : '',
                        freezeHeader ? styles.stickyTop : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={frozen ? frozenHeaderStyle(colIdx, size) : { width: size, minWidth: size }}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      onKeyDown={canSort ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          header.column.getToggleSortingHandler()?.(e as any);
                        }
                      } : undefined}
                      tabIndex={canSort ? 0 : undefined}
                      aria-sort={
                        sortDir === 'asc'
                          ? 'ascending'
                          : sortDir === 'desc'
                          ? 'descending'
                          : canSort
                          ? 'none'
                          : undefined
                      }
                      scope="col"
                    >
                      <div className={styles.thContent}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className={styles.sortIconWrap} aria-hidden="true">
                            <img
                              className={sortDir ? styles.sortIconActive : styles.sortIcon}
                              src={sortDir === 'asc' ? sortAsc : sortDir === 'desc' ? sortDesc : sortDefault}
                              alt=""
                            />
                          </span>
                        )}
                      </div>
                      {!isExpander && (
                        <div
                          className={[
                            styles.resizeHandle,
                            header.column.getIsResizing() ? styles.resizing : '',
                          ].filter(Boolean).join(' ')}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          aria-hidden="true"
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isAlt = row.index % 2 === 1;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={[
                      styles.tr,
                      isAlt ? styles.rowAlt : '',
                      styles.clickable,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      row.toggleExpanded();
                      if (onPointClick) handleRowClick(row);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        row.toggleExpanded();
                        if (onPointClick) handleRowClick(row);
                      }
                    }}
                    tabIndex={0}
                    aria-rowindex={row.index + 2}
                    aria-expanded={row.getIsExpanded()}
                  >
                    {row.getVisibleCells().map((cell, colIdx) => {
                      const isExpander = cell.column.id === '__expander';
                      const frozen = isFrozen(colIdx);

                      const cellSize = cell.column.getSize();

                      if (isExpander) {
                        return (
                          <td
                            key={cell.id}
                            className={[
                              styles.tdExpander,
                              frozen ? styles.frozenCell : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            style={frozen ? frozenStyle(0, cellSize) : { width: cellSize, minWidth: cellSize }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      }

                      // colIdx is 1-based for data columns (expander is 0),
                      // which matches the user-facing 1-based columnColors keys.
                      const colColor = columnColors[colIdx];

                      const colorStyle: React.CSSProperties = colColor
                        ? {
                            backgroundColor: hexWithAlpha(colColor, 0.12),
                            borderLeft: `3px solid ${colColor}`,
                          }
                        : {};

                      return (
                        <td
                          key={cell.id}
                          className={[
                            styles.td,
                            frozen ? styles.frozenCell : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={
                            frozen
                              ? { ...frozenStyle(colIdx, cellSize), ...colorStyle }
                              : { width: cellSize, minWidth: cellSize, ...colorStyle }
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>

                  {row.getIsExpanded() && (
                    <tr className={styles.expandedRow} aria-rowindex={row.index + 2}>
                      <td colSpan={columns.length} className={styles.expandedCell}>
                        <div className={styles.expandedContent} style={wrapperWidth ? { maxWidth: wrapperWidth } : undefined}>
                          {orderedColumns.map((col) => {
                            const val = row.original[col];
                            const isLong = typeof val === 'string' && val.length > 80;
                            return (
                              <div
                                key={col}
                                className={isLong ? styles.expandedItemFull : styles.expandedItem}
                              >
                                <span className={styles.expandedKey}>{col}</span>
                                <span className={isLong ? styles.expandedValueLong : styles.expandedValue}>
                                  {formatValue(val, col)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>

          {showFooter && (
            <tfoot>
              {table.getFooterGroups().map((fg) => (
                <tr key={fg.id}>
                  {fg.headers.map((header, colIdx) => {
                    const isExpander = header.column.id === '__expander';
                    const frozen = isFrozen(colIdx);
                    const footerSize = header.getSize();

                    if (isExpander) {
                      return (
                        <td
                          key={header.id}
                          className={[
                            styles.tfExpander,
                            freezeFooter ? styles.stickyBottom : '',
                            frozen ? styles.frozenCell : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={frozen ? frozenStyle(0, footerSize) : { width: footerSize, minWidth: footerSize }}
                        />
                      );
                    }

                    return (
                      <td
                        key={header.id}
                        className={[
                          styles.tf,
                          freezeFooter ? styles.stickyBottom : '',
                          frozen ? styles.frozenCell : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={frozen ? frozenStyle(colIdx, footerSize) : { width: footerSize, minWidth: footerSize }}
                      >
                        {flexRender(header.column.columnDef.footer, header.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      <nav className={styles.pagination} aria-label={t('Table pagination')}>
        <span className={styles.pageInfo}>
          {totalRows === 0 ? t('0 rows') : t('{{first}}–{{last}} of {{total}}', { first: firstRow, last: lastRow, total: totalRows })}
        </span>

        <div className={styles.pageControls}>
          {!isServerPaginated && (
            <select
              className={styles.pageSizeSelect}
              value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              aria-label={t('Rows per page')}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          )}

          <button
            className={styles.pageBtn}
            onClick={isServerPaginated ? () => onPageChange!(1) : () => table.firstPage()}
            disabled={isServerPaginated ? serverPage! <= 1 : !table.getCanPreviousPage()}
            aria-label={t('First page')}
            type="button"
          >
            «
          </button>
          <button
            className={styles.pageBtn}
            onClick={isServerPaginated ? () => onPageChange!(serverPage! - 1) : () => table.previousPage()}
            disabled={isServerPaginated ? serverPage! <= 1 : !table.getCanPreviousPage()}
            aria-label={t('Previous page')}
            type="button"
          >
            ‹
          </button>

          <span className={styles.pageNum} aria-live="polite" aria-atomic="true">
            {currentPageIndex + 1} / {totalPages}
          </span>

          <button
            className={styles.pageBtn}
            onClick={isServerPaginated ? () => onPageChange!(serverPage! + 1) : () => table.nextPage()}
            disabled={isServerPaginated ? serverPage! >= serverTotalPages : !table.getCanNextPage()}
            aria-label={t('Next page')}
            type="button"
          >
            ›
          </button>
          <button
            className={styles.pageBtn}
            onClick={isServerPaginated ? () => onPageChange!(serverTotalPages) : () => table.lastPage()}
            disabled={isServerPaginated ? serverPage! >= serverTotalPages : !table.getCanNextPage()}
            aria-label={t('Last page')}
            type="button"
          >
            »
          </button>
        </div>
      </nav>
    </div>
  );
};
